import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

export const DEFAULT_DISPLAY_LOCALE = "tr-TR";
export const DEFAULT_DISPLAY_TIME_ZONE = "Europe/Istanbul";

export type DisplayLocalization = {
  timeZone: string;
  locale: string;
};

let currentLocalization: DisplayLocalization = {
  timeZone: DEFAULT_DISPLAY_TIME_ZONE,
  locale: DEFAULT_DISPLAY_LOCALE,
};

export function setDisplayLocalization(next: Partial<DisplayLocalization>) {
  currentLocalization = {
    timeZone: next.timeZone?.trim() || DEFAULT_DISPLAY_TIME_ZONE,
    locale: next.locale?.trim() || DEFAULT_DISPLAY_LOCALE,
  };
}

export function getDisplayLocalization(): DisplayLocalization {
  return currentLocalization;
}

export type DateTimeFormatVariant =
  | "short"
  | "medium"
  | "time"
  | "timeSeconds"
  | "date"
  | "clockDate";

const VARIANT_OPTIONS: Record<DateTimeFormatVariant, Intl.DateTimeFormatOptions> = {
  short: {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  },
  medium: {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  },
  time: {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  },
  timeSeconds: {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  },
  date: {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  },
  clockDate: {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  },
};

const NAIVE_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;
const NAIVE_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** GET payloads: offset/Z kept as-is; naive values are UTC as stored in DB. */
export function toDate(value?: string | Date | number | null): Date | null {
  if (value == null || value === "") {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const trimmed = String(value).trim();
  if (NAIVE_DATE_TIME.test(trimmed)) {
    const date = new Date(`${trimmed}Z`);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (NAIVE_DATE.test(trimmed)) {
    const date = new Date(`${trimmed}T00:00:00Z`);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(trimmed);
  return Number.isNaN(date.getTime()) ? null : date;
}

function nowInZone(timeZone: string = currentLocalization.timeZone) {
  try {
    return dayjs().tz(timeZone);
  } catch {
    return dayjs.utc();
  }
}

export function formatDateTime(
  value?: string | Date | number | null,
  variant: DateTimeFormatVariant = "short",
  localization: DisplayLocalization = currentLocalization,
): string {
  const date = toDate(value);
  if (!date) {
    return "";
  }
  try {
    return new Intl.DateTimeFormat(localization.locale, {
      ...VARIANT_OPTIONS[variant],
      timeZone: localization.timeZone,
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat(DEFAULT_DISPLAY_LOCALE, {
      ...VARIANT_OPTIONS[variant],
      timeZone: DEFAULT_DISPLAY_TIME_ZONE,
    }).format(date);
  }
}

export function formatChartTimeLabel(value?: string | Date | number | null): string {
  return formatDateTime(value, "time") || (value == null ? "" : String(value));
}

export function formatRelativeFromNow(value?: string | Date | number | null): string {
  const date = toDate(value);
  return date ? dayjs(date).fromNow() : "";
}

export function resolveDisplayLocalization(
  customer?: { timeZone?: string; locale?: string } | null,
  fallback: DisplayLocalization = currentLocalization,
): DisplayLocalization {
  return {
    timeZone: customer?.timeZone?.trim() || fallback.timeZone,
    locale: customer?.locale?.trim() || fallback.locale,
  };
}

export function formatTimeZoneLabel(
  timeZone: string = currentLocalization.timeZone,
  locale: string = currentLocalization.locale,
  at: Date = new Date(),
  timeZoneName: Intl.DateTimeFormatOptions["timeZoneName"] = "short",
): string {
  try {
    const parts = new Intl.DateTimeFormat(locale, {
      timeZone,
      timeZoneName,
    }).formatToParts(at);
    return parts.find((part) => part.type === "timeZoneName")?.value || timeZone;
  } catch {
    return timeZone;
  }
}

export function formatNowLabel(locale: string = currentLocalization.locale): string {
  const language = locale.split("-")[0]?.toLowerCase();
  switch (language) {
    case "tr":
      return "Şimdi";
    case "de":
      return "Jetzt";
    case "fr":
      return "Maintenant";
    case "ar":
      return "الآن";
    default:
      return "Now";
  }
}

/** Instant from the API → wall-clock dayjs in the customer timezone (pickers / charts). */
export function toDisplayDayjs(
  value?: string | Date | number | null,
  timeZone: string = currentLocalization.timeZone,
) {
  const date = toDate(value ?? null);
  if (!date) {
    return null;
  }
  try {
    return dayjs(date).tz(timeZone);
  } catch {
    return dayjs(date).utc();
  }
}

/** Customer TZ wall clock → UTC ISO local datetime (`YYYY-MM-DDTHH:mm:ss`) for writes. */
export function toApiUtcDateTime(
  value?: string | Date | dayjs.Dayjs | null,
  timeZone: string = currentLocalization.timeZone,
): string | undefined {
  if (value == null || value === "") {
    return undefined;
  }
  const parsed = dayjs.isDayjs(value) ? value : dayjs(value);
  if (!parsed.isValid()) {
    return undefined;
  }
  const wall = parsed.format("YYYY-MM-DDTHH:mm:ss");
  try {
    return dayjs.tz(wall, timeZone).utc().format("YYYY-MM-DDTHH:mm:ss");
  } catch {
    return parsed.utc().format("YYYY-MM-DDTHH:mm:ss");
  }
}

/** Customer TZ wall clock → UTC instant with `Z` (query params / search ranges). */
export function toApiUtcIso(
  value?: string | Date | dayjs.Dayjs | null,
  timeZone: string = currentLocalization.timeZone,
): string | undefined {
  const naive = toApiUtcDateTime(value, timeZone);
  return naive ? `${naive}Z` : undefined;
}

export function toApiUtcDayStart(
  value: string | Date,
  timeZone: string = currentLocalization.timeZone,
): string {
  const parsed = dayjs(value);
  const wall = `${parsed.format("YYYY-MM-DD")}T00:00:00`;
  try {
    return dayjs.tz(wall, timeZone).utc().format("YYYY-MM-DDTHH:mm:ss");
  } catch {
    return `${parsed.format("YYYY-MM-DD")}T00:00:00`;
  }
}

export function toApiUtcDayEnd(
  value: string | Date,
  timeZone: string = currentLocalization.timeZone,
): string {
  const parsed = dayjs(value);
  const wall = `${parsed.format("YYYY-MM-DD")}T23:59:59`;
  try {
    return dayjs.tz(wall, timeZone).utc().format("YYYY-MM-DDTHH:mm:ss");
  } catch {
    return `${parsed.format("YYYY-MM-DD")}T23:59:59`;
  }
}

export type DisplayCalendarRange = {
  gte: string;
  lte: string;
  start: Date;
  end: Date;
};

/** Customer-TZ calendar day from 00:00 through now (dashboard "today" window). */
export function getDisplayCalendarDayWindow(
  dayOffset = 0,
  timeZone: string = currentLocalization.timeZone,
): DisplayCalendarRange {
  const now = nowInZone(timeZone);
  const anchor = now.add(dayOffset, "day");
  const start = anchor.startOf("day");
  const end = dayOffset === 0 ? now : now.add(dayOffset, "day");
  return {
    gte: start.toISOString(),
    lte: end.toISOString(),
    start: start.toDate(),
    end: end.toDate(),
  };
}

/** Customer-TZ full calendar day (00:00:00–23:59:59.999). */
export function getDisplayCalendarDayBounds(
  dayOffset = 0,
  timeZone: string = currentLocalization.timeZone,
): DisplayCalendarRange {
  const anchor = nowInZone(timeZone).add(dayOffset, "day");
  const start = anchor.startOf("day");
  const end = anchor.endOf("day");
  return {
    gte: start.toISOString(),
    lte: end.toISOString(),
    start: start.toDate(),
    end: end.toDate(),
  };
}

export function getDisplayHourStartIso(timeZone: string = currentLocalization.timeZone): string {
  return nowInZone(timeZone).startOf("hour").toISOString();
}

export function getDisplayWeekday(
  value?: string | Date | number | null,
  timeZone: string = currentLocalization.timeZone,
): number | null {
  const zoned = toDisplayDayjs(value, timeZone);
  return zoned ? zoned.day() : null;
}

export function listTimeZones(): string[] {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return ["UTC", "Europe/Istanbul", "Europe/London", "Europe/Berlin", "America/New_York"];
  }
}

export function listLocales(): string[] {
  try {
    return (Intl.supportedValuesOf as (key: string) => string[])("locale");
  } catch {
    return ["tr-TR", "en-US", "en-GB", "de-DE", "fr-FR", "ar-SA"];
  }
}
